import { User } from '@state/infrastructure/User'
import axios from '@utils/axiosInstance'
import { siteClearance } from '@utils/clearanceHelpers'

import { getProfile } from '@/services/auth.service'

const BASE = process.env.VUE_APP_BACKEND_PUBLIC_URL

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  authToken: getSavedState('auth.token'),
  idToken: getSavedState('auth.idToken'),
  refreshToken: getSavedState('auth.refreshToken'),
  actorRoles: getSavedState('auth.actorRoles'),
  actorPermissions: getSavedState('auth.actorPermissions'),
  fedIdUser: null,
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
}

export const mutations = {
  updateUser(state, fedIdUser) {
    state.fedIdUser = fedIdUser
  },
  SET_CURRENT_USER(state, newValue) {
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
  },
  SET_AUTH_TOKEN(state, token) {
    state.authToken = token
    saveState('auth.token', token)
  },
  SET_ID_TOKEN(state, idToken) {
    state.idToken = idToken
    saveState('auth.idToken', idToken)
  },
  SET_REFRESH_TOKEN(state, refreshToken) {
    state.refreshToken = refreshToken
    saveState('auth.refreshToken', refreshToken)
  },
  SET_ACTOR_ROLES(state, actorRoles) {
    state.actorRoles = actorRoles
    saveState('auth.actorRoles', actorRoles)
  },
  SET_ACTOR_PERMISSIONS(state, permissions) {
    state.actorPermissions = permissions
    saveState('auth.actorPermissions', permissions)
  },
  LOGOUT(state) {
    state.currentUser = null
    state.authToken = null
    state.idToken = null
    state.refreshToken = null
    state.actorRoles = null
    state.actorPermissions = null
    state.fedIdUser = null
    clearStorage()
  },
  SET_LAST_LOGIN_DATA(state, lastLogin) {
    state.lastLogin = lastLogin
    saveState('auth.lastLogin', lastLogin)
  },
}

export const actions = {
  async sendTokenV2({ commit }, token) {
    if (token) {
      commit('SET_AUTH_TOKEN', token)
      axios.defaults.headers.common.Authorization = `Bearer ${token}`

      const profile = await getProfile({
        headers: { Authorization: `Bearer ${token}` },
      })

      commit('SET_CURRENT_USER', profile.user)
      commit('SET_ACTOR_ROLES', profile.user.roles)
      commit('SET_ACTOR_PERMISSIONS', profile.user.permissions ?? null)
    }
  },

  async sendRefreshToken({ commit }, payload) {
    try {
      const response = await axios.post(`${BASE}/api/v2/actor/idpDecathlonOxylane/getJwtFromRefreshToken`, payload)

      const AUTH_TOKEN = response.data.accessToken
      const REFRESH_TOKEN = response.data.refreshToken
      commit('SET_AUTH_TOKEN', AUTH_TOKEN)
      commit('SET_REFRESH_TOKEN', REFRESH_TOKEN)

      const token = JSON.parse(localStorage.getItem('auth.token'))
      if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`
      }

      return response
    } catch (e) {
      throw Error(`Auth error ocurred in ${BASE}/api/v1/common/idpDecathlonOxylane/getJwtFromRefreshToken ${e}`)
    }
  },

  logInFedId({ commit, dispatch, getters }, fedIdUser) {
    if (getters.loggedIn) return dispatch('validate')
    commit('SET_CURRENT_USER', fedIdUser)
  },

  // TODO: get profile for Current User @v2
  async fetchSelfProfile({ commit }) {
    try {
      const response = await axios.get(`${BASE}/api/v2/actor/profile`)
      const ACTOR_ROLES = response.data.actorRoles
      commit('SET_ACTOR_ROLES', ACTOR_ROLES)
    } catch (error) {
      throw new Error(`API Error occurred at /api/v2/actor/profile`)
    }
  },

  // Logs out the current user.
  logOut({ commit }) {
    commit('LOGOUT')
  },

  // TODO: validate user token @v2
  validate({ commit, state }) {
    if (!state.currentUser) return Promise.resolve(null)
    const user = new User(
      state.currentUser.aud,
      state.currentUser.auth_time,
      state.currentUser.c,
      state.currentUser.email,
      state.currentUser.exp,
      state.currentUser.familyName,
      state.currentUser.federation_idp,
      state.currentUser.givenName,
      state.currentUser.iat,
      state.currentUser.iss,
      state.currentUser.jti,
      state.currentUser.mail,
      state.currentUser.name,
      state.currentUser.objectclass,
      state.currentUser['pi.sri'],
      state.currentUser.preferredLanguage,
      state.currentUser.sn,
      state.currentUser.sub,
      state.currentUser.uid,
      state.currentUser.uuid,
    )
    if (state.currentUser.uuid !== user.uuid || state.currentUser.uid !== user.uid) {
      commit('LOGOUT')
    } else {
      return Promise.resolve(user)
    }
  },
}

// Look in actorRoles if current user can work on the requested site. Always true for admins.
export function isAllowedOnSite(siteCode) {
  const actorRole = state.actorRoles
  if (!actorRole) return false

  if (siteClearance.admin.includes(actorRole.code)) return true

  if (!siteCode || !state.siteCode) return false
  return state.siteCode === siteCode
}

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function clearStorage(key) {
  window.localStorage.clear()
}
